import { useEffect, useState, useCallback } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Search, Loader2, PackageCheck, Truck, CalendarCheck, ClipboardList, XCircle, CalendarDays } from 'lucide-react';
import { storeApi } from '../../lib/api';
import { formatAUD } from './CartContext';
import { InvoiceCard } from './InvoiceCard';

export const STATUS_STEPS = [
  { key: 'new', label: 'Received', icon: ClipboardList },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle2 },
  { key: 'scheduled', label: 'Scheduled', icon: CalendarCheck },
  { key: 'delivered', label: 'Delivered', icon: PackageCheck },
];

function StatusTimeline({ status }) {
  if (status === 'cancelled') {
    return <div className="flex items-center gap-2 text-red-600 font-semibold" data-testid="order-status-cancelled"><XCircle className="h-5 w-5" /> This order was cancelled</div>;
  }
  const idx = STATUS_STEPS.findIndex((s) => s.key === status);
  return (
    <ol className="grid grid-cols-4 gap-2" data-testid="order-timeline">
      {STATUS_STEPS.map((s, i) => {
        const done = i <= idx;
        return (
          <li key={s.key} className="text-center">
            <div className={`h-1.5 rounded-full mb-3 ${done ? 'bg-[var(--s-green-3)]' : 'bg-[var(--s-sand)]'}`} />
            <s.icon className={`h-5 w-5 mx-auto mb-1 ${done ? 'text-[var(--s-green)]' : 'text-neutral-300'}`} />
            <p className={`text-xs font-semibold ${done ? '' : 'text-neutral-400'}`}>{s.label}</p>
          </li>
        );
      })}
    </ol>
  );
}

const niceDate = (iso) => { try { return new Date(`${iso}T12:00:00`).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); } catch { return iso; } };

function BookedDateNotice({ order }) {
  const kind = order.delivery_method === 'pickup' ? 'pickup' : 'delivery';
  if (order.status === 'cancelled') return null;
  if (order.status === 'delivered') {
    return <div className="flex items-center gap-3 rounded-xl px-5 py-4 bg-green-50 text-green-900" data-testid="delivered-notice"><PackageCheck className="h-6 w-6 shrink-0" /><p className="font-heading font-bold">Delivered{order.scheduled_date ? ` on ${niceDate(order.scheduled_date)}` : ''} — enjoy your new lawn!</p></div>;
  }
  if (order.scheduled_date && ['confirmed', 'scheduled'].includes(order.status)) {
    return (
      <div className="flex items-center gap-3 rounded-xl px-5 py-4 text-white" style={{ background: 'var(--s-green-2)' }} data-testid="booked-date-notice">
        <CalendarDays className="h-7 w-7 shrink-0" />
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-white/70">Your {kind} is booked</p>
          <p className="font-heading font-bold text-lg leading-tight" data-testid="booked-date">{niceDate(order.scheduled_date)}</p>
          <p className="text-xs text-white/70 mt-0.5">{kind === 'pickup' ? 'Collect from the farm — call ahead if your plans change.' : 'Please have the area prepared and someone home to receive the turf.'}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 rounded-xl px-5 py-4" style={{ background: 'var(--s-sand)' }} data-testid="pending-date-notice">
      <CalendarDays className="h-6 w-6 shrink-0" style={{ color: 'var(--s-green)' }} />
      <p className="text-sm">{order.preferred_date ? <>You asked for <strong>{niceDate(order.preferred_date)}</strong> — we'll confirm your {kind} date shortly.</> : <>We'll call to confirm your {kind} date.</>}</p>
    </div>
  );
}

function OrderDetails({ order }) {
  return (
    <div className="bg-white rounded-2xl p-6 lg:p-8 space-y-8" style={{ border: '1px solid var(--s-sand)' }} data-testid="order-details">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <p className="store-eyebrow mb-1">Order number</p>
          <p className="text-2xl font-heading font-bold" data-testid="order-number">{order.order_number}</p>
        </div>
        <span className="store-pill capitalize" data-testid="order-status">{order.status}</span>
      </div>

      <StatusTimeline status={order.status} />

      <BookedDateNotice order={order} />

      <div className="grid sm:grid-cols-2 gap-6 text-sm">
        <div>
          <p className="font-semibold mb-1">Customer</p>
          <p style={{ color: 'var(--s-muted)' }}>{order.customer_name}<br />{order.email}<br />{order.phone}</p>
        </div>
        <div>
          <p className="font-semibold mb-1 flex items-center gap-1.5"><Truck className="h-4 w-4" /> {order.delivery_method === 'pickup' ? 'Farm pickup' : 'Delivery'}</p>
          <p style={{ color: 'var(--s-muted)' }}>
            {order.delivery_method === 'pickup' ? 'Collect from Greenhill Creek Turf' : [order.address, order.suburb, order.postcode].filter(Boolean).join(', ')}
            {order.scheduled_date && ['confirmed', 'scheduled', 'delivered'].includes(order.status) ? <><br />{order.status === 'delivered' ? 'Delivered' : 'Booked'}: {niceDate(order.scheduled_date)}</> : order.preferred_date && order.status !== 'cancelled' ? <><br />Preferred date: {niceDate(order.preferred_date)}</> : null}
          </p>
        </div>
      </div>

      <div>
        <p className="font-semibold text-sm mb-3">Turf</p>
        <ul className="divide-y rounded-xl overflow-hidden" style={{ borderColor: 'var(--s-sand)', background: 'var(--s-cream)' }}>
          {order.items.map((i) => (
            <li key={i.variety_id} className="flex justify-between gap-3 px-4 py-3 text-sm">
              <span>{i.variety_name} <span style={{ color: 'var(--s-muted)' }}>× {i.quantity_m2} m² @ {formatAUD(i.price_per_m2)}</span></span>
              <span className="font-semibold">{formatAUD(i.line_total)}</span>
            </li>
          ))}
          <li className="flex justify-between gap-3 px-4 py-3 text-sm">
            <span>Turf ({order.total_m2} m²)</span><span className="font-semibold" data-testid="order-subtotal">{formatAUD(order.subtotal)}</span>
          </li>
          <li className="flex justify-between gap-3 px-4 py-3 text-sm">
            <span>{order.delivery_method === 'pickup' ? 'Farm pickup' : `Delivery${order.delivery_zone ? ` · ${order.delivery_zone}` : ''}`}</span>
            <span className="font-semibold" data-testid="order-delivery-fee">
              {order.delivery_method === 'pickup' ? 'Free' : order.delivery_fee == null ? 'Quote to be confirmed' : order.delivery_fee === 0 ? 'Free' : formatAUD(order.delivery_fee)}
            </span>
          </li>
          <li className="flex justify-between gap-3 px-4 py-3 font-heading font-bold" style={{ background: 'var(--s-sand)' }}>
            <span>{order.delivery_method === 'delivery' && order.delivery_fee == null ? 'Turf total' : 'Total'}</span><span data-testid="order-total">{formatAUD(order.total)}</span>
          </li>
        </ul>
        {order.delivery_method === 'delivery' && order.delivery_fee == null && <p className="text-xs mt-2" style={{ color: 'var(--s-muted)' }}>Your suburb isn't in a set delivery zone yet — we'll confirm the delivery price when we call.</p>}
      </div>

      {order.notes && <p className="text-sm"><span className="font-semibold">Your notes:</span> <span style={{ color: 'var(--s-muted)' }}>{order.notes}</span></p>}
    </div>
  );
}

export default function OrderStatusPage() {
  const [params] = useSearchParams();
  const location = useLocation();
  const justPlaced = location.state?.justPlaced;
  const [orderNumber, setOrderNumber] = useState(params.get('order') || '');
  const [email, setEmail] = useState(params.get('email') || '');
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const lookup = useCallback(async (num, mail) => {
    if (!num || !mail) return;
    setLoading(true); setError('');
    try { const { data } = await storeApi.trackOrder(num, mail); setOrder(data); }
    catch (err) { setOrder(null); setError(err.response?.data?.detail || 'Could not find that order'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { lookup(params.get('order'), params.get('email')); }, [params, lookup]);

  const sessionId = params.get('session_id');
  const refetch = useCallback(() => lookup(params.get('order'), params.get('email')), [params, lookup]);

  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-8 py-10 lg:py-16" data-testid="track-order-page">
      {justPlaced && order && (
        <div className="rounded-2xl p-6 mb-8 text-white flex gap-4 store-rise" style={{ background: 'var(--s-green)' }} data-testid="order-success-banner">
          <CheckCircle2 className="h-8 w-8 shrink-0" style={{ color: '#95d5b2' }} />
          <div>
            <h1 className="font-heading font-bold text-2xl mb-1">Thanks {order.customer_name.split(' ')[0]}, we've got your order!</h1>
            <p className="text-white/80 text-sm">We'll call you on {order.phone} to confirm delivery and pricing. Keep your order number handy to check progress any time.</p>
          </div>
        </div>
      )}

      {!justPlaced && (
        <>
          <p className="store-eyebrow mb-3">Track your order</p>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-8">Where's my turf?</h1>
        </>
      )}

      <form onSubmit={(e) => { e.preventDefault(); lookup(orderNumber, email); }} className="bg-white rounded-2xl p-6 mb-8 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end" style={{ border: '1px solid var(--s-sand)' }} data-testid="track-form">
        <label className="text-sm"><span className="block font-medium mb-1.5">Order number</span><input value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="GCT-20260601-AB12" className="store-input uppercase" data-testid="track-order-input" /></label>
        <label className="text-sm"><span className="block font-medium mb-1.5">Email used on order</span><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="store-input" data-testid="track-email-input" /></label>
        <button type="submit" disabled={loading} className="store-btn store-btn-dark py-3" data-testid="track-submit-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Find</button>
      </form>

      {error && <p className="text-sm text-red-600 mb-6" data-testid="track-error">{error}</p>}
      {order?.invoice && order.invoice.payment_status !== 'void' && <div className="mb-8"><InvoiceCard order={order} sessionId={sessionId} paymentCancelled={params.get('payment') === 'cancelled'} onPaid={refetch} /></div>}
      {order && <OrderDetails order={order} />}

      <p className="text-sm mt-10 text-center" style={{ color: 'var(--s-muted)' }}>Need more turf? <Link to="/" className="font-semibold underline" style={{ color: 'var(--s-green)' }}>Browse the range</Link></p>
    </div>
  );
}
